export const RECORD_ACHIEVEMENT_IDS = Object.freeze({
  WRITING_TRAINING: 'writingTraining',
  MARKET_UPLOADS: 'marketUploads',
  LISTENING_TIME: 'listeningTime',
  LEVEL_100: 'level100',
  GRADUATION_1000: 'graduation1000',
  MEMBERSHIP_YEAR: 'membershipYear',
  TOTAL_SCORE: 'totalScore',
  TOTAL_REPEATS: 'totalRepeats',
  TOTAL_TIME: 'totalPracticeTime',
  TOTAL_DAYS: 'totalDays',
  MAX_STREAK: 'maxStreak',
  MAX_DAILY_SCORE: 'maxDailyScore',
  MAX_DAILY_TIME: 'maxDailyPracticeTime',
  TOURNAMENT_SCORE: 'tournamentScore',
  TOURNAMENT_ROUND: 'tournamentChampion',
  TOURNAMENT_ENTRIES: 'tournamentEntries',
  TOURNAMENT_WIN_RATE: 'tournamentWinRate',
});

export const RECORD_ACHIEVEMENT_DEFINITIONS = Object.freeze([
  { id: RECORD_ACHIEVEMENT_IDS.WRITING_TRAINING, threshold: 50, tiers: [50, 100, 150] },
  { id: RECORD_ACHIEVEMENT_IDS.MARKET_UPLOADS, threshold: 3 },
  { id: RECORD_ACHIEVEMENT_IDS.LISTENING_TIME, threshold: 18000 },
  { id: RECORD_ACHIEVEMENT_IDS.LEVEL_100, threshold: 100, tiers: [100, 200, 300] },
  { id: RECORD_ACHIEVEMENT_IDS.GRADUATION_1000, threshold: 1000, tiers: [1000, 2000, 3000] },
  { id: RECORD_ACHIEVEMENT_IDS.MEMBERSHIP_YEAR, threshold: 1, tiers: [1, 2, 3] },
  { id: RECORD_ACHIEVEMENT_IDS.TOTAL_SCORE, threshold: 200000, tiers: [200000, 400000, 600000] },
  { id: RECORD_ACHIEVEMENT_IDS.TOTAL_REPEATS, threshold: 100000, tiers: [100000, 200000, 300000] },
  { id: RECORD_ACHIEVEMENT_IDS.TOTAL_TIME, threshold: 360000, tiers: [360000, 720000, 1080000] },
  { id: RECORD_ACHIEVEMENT_IDS.TOTAL_DAYS, threshold: 100, tiers: [100, 200, 300] },
  { id: RECORD_ACHIEVEMENT_IDS.MAX_STREAK, threshold: 50 },
  { id: RECORD_ACHIEVEMENT_IDS.MAX_DAILY_SCORE, threshold: 3000 },
  { id: RECORD_ACHIEVEMENT_IDS.MAX_DAILY_TIME, threshold: 7200 },
  { id: RECORD_ACHIEVEMENT_IDS.TOURNAMENT_SCORE, threshold: 20000 },
  { id: RECORD_ACHIEVEMENT_IDS.TOURNAMENT_ROUND },
  { id: RECORD_ACHIEVEMENT_IDS.TOURNAMENT_ENTRIES, threshold: 5 },
  { id: RECORD_ACHIEVEMENT_IDS.TOURNAMENT_WIN_RATE, threshold: 60 },
]);

function hasChampionRound(tournament = {}) {
  if (tournament.bestRoundCode === 'champion') return true;
  const normalized = typeof tournament.bestRound === 'string'
    ? tournament.bestRound.toLowerCase().replace(/[\s-]/g, '')
    : '';
  return ['championship', 'champion', '우승'].includes(normalized);
}

export function getRecordAchievementConditions({ summary = {}, achievements = {}, tournament = {} } = {}) {
  return {
    [RECORD_ACHIEVEMENT_IDS.WRITING_TRAINING]: Number(summary.writingTrainingCount) >= 50,
    [RECORD_ACHIEVEMENT_IDS.MARKET_UPLOADS]: Number(summary.marketUploadCount) >= 3,
    [RECORD_ACHIEVEMENT_IDS.LISTENING_TIME]: Number(summary.totalListeningTime) >= 18000,
    [RECORD_ACHIEVEMENT_IDS.LEVEL_100]: Number(summary.level) >= 100,
    [RECORD_ACHIEVEMENT_IDS.GRADUATION_1000]: Number(summary.graduatedCardCount) >= 1000,
    [RECORD_ACHIEVEMENT_IDS.MEMBERSHIP_YEAR]: Number(summary.membershipYears) >= 1 || summary.membershipYearCompleted === true,
    [RECORD_ACHIEVEMENT_IDS.TOTAL_SCORE]: Number(summary.totalScoreGained ?? summary.exp) >= 200000,
    [RECORD_ACHIEVEMENT_IDS.TOTAL_REPEATS]: Number(summary.totalRepeatCount) >= 100000,
    [RECORD_ACHIEVEMENT_IDS.TOTAL_TIME]: Number(summary.totalPracticeTime) >= 360000,
    [RECORD_ACHIEVEMENT_IDS.TOTAL_DAYS]: Number(summary.totalDays) >= 100,
    [RECORD_ACHIEVEMENT_IDS.MAX_STREAK]: Number(achievements.maxContinuousPracticeDays) >= 50,
    [RECORD_ACHIEVEMENT_IDS.MAX_DAILY_SCORE]: Number(achievements.maxScorePerDay) >= 3000,
    [RECORD_ACHIEVEMENT_IDS.MAX_DAILY_TIME]: Number(achievements.maxPracticeTimePerDay) >= 7200,
    [RECORD_ACHIEVEMENT_IDS.TOURNAMENT_SCORE]: Number(tournament.maxTournamentScore) >= 20000,
    [RECORD_ACHIEVEMENT_IDS.TOURNAMENT_ROUND]: hasChampionRound(tournament),
    [RECORD_ACHIEVEMENT_IDS.TOURNAMENT_ENTRIES]: Number(tournament.attendedCount) >= 5,
    [RECORD_ACHIEVEMENT_IDS.TOURNAMENT_WIN_RATE]: Number(tournament.winningPercent) >= 60,
  };
}

export function getRecordAchievementState(profile = {}) {
  const achievements = profile?.achievements || {};
  const serverState = achievements.unlocked;
  const conditions = getRecordAchievementConditions({
    summary: profile?.summary || {},
    achievements,
    tournament: profile?.tournament || {},
  });

  return Object.fromEntries(RECORD_ACHIEVEMENT_DEFINITIONS.map(({ id }) => [
    id,
    serverState?.[id] === true || Number(achievements.tiers?.[id]) > 0 || conditions[id] === true,
  ]));
}

export const RECORD_TIER_NAMES = ['bronze', 'silver', 'gold'];

export function getRecordAchievementTiers(profile = {}) {
  const summary = profile?.summary || {};
  const values = {
    writingTraining: summary.writingTrainingCount,
    totalScore: summary.totalScoreGained ?? summary.exp,
    totalRepeats: summary.totalRepeatCount,
    totalPracticeTime: summary.totalPracticeTime,
    totalDays: summary.totalDays,
    level100: summary.level,
    graduation1000: summary.graduatedCardCount,
    membershipYear: summary.membershipYears ?? (summary.membershipYearCompleted ? 1 : 0),
  };
  const unlocked = getRecordAchievementState(profile);
  return Object.fromEntries(RECORD_ACHIEVEMENT_DEFINITIONS.filter(({ tiers }) => tiers).map(({ id, tiers }) => [
    id, Math.max(
      tiers.filter((threshold) => Number(values[id]) >= threshold).length,
      Math.min(3, Math.max(0, Math.floor(Number(profile?.achievements?.tiers?.[id]) || 0))),
      unlocked[id] ? 1 : 0,
    ),
  ]));
}

export function getNewlyUnlockedRecordAchievementIds(previousProfile, nextProfile) {
  const previous = getRecordAchievementState(previousProfile);
  const next = getRecordAchievementState(nextProfile);
  return RECORD_ACHIEVEMENT_DEFINITIONS
    .map(({ id }) => id)
    .filter((id) => !previous[id] && next[id]);
}
